import { createContext, ReactNode, useMemo } from 'react';

export type PageTheme =
  | 'default'
  | 'employers'
  | 'upskillers'
  | 'careerStarters'
  | 'reskillers';

export type PrimaryThemeColours =
  | 'ultraviolet'
  | 'electron'
  | 'supernova'
  | 'aurora'
  | 'molten';

export const coloursByTheme = {
  default: {
    primary: 'ultraviolet',
    secondary: 'ultraviolet-tint',
  },
  employers: {
    primary: 'electron',
    secondary: 'electron-tint',
  },
  upskillers: {
    primary: 'supernova',
    secondary: 'supernova-tint',
  },
  careerStarters: {
    primary: 'aurora',
    secondary: 'aurora-tint',
  },
  reskillers: {
    primary: 'molten',
    secondary: 'molten-tint',
  },
};

export const PageThemeContext = createContext(null);

interface PageThemeContext {
  pageTheme: PageTheme | null;
  primaryColour?: string;
  secondaryColour?: string;
  pickColour: (
    blockFieldColour: string,
    themeColourIndex: string,
    defaultColour: string,
  ) => string;
}

interface PageThemeContextProviderProps {
  children: ReactNode;
  pageTheme: PageTheme | null;
}

export const PageThemeContextProvider = ({
  children,
  pageTheme,
}: PageThemeContextProviderProps) => {
  const pageThemeProviderValue = useMemo<PageThemeContext>(
    () => ({
      pageTheme,
      primaryColour: coloursByTheme[pageTheme]?.primary,
      secondaryColour: coloursByTheme[pageTheme]?.secondary,
      pickColour: (blockFieldColour, themeColourIndex, defaultColour) => {
        return (
          blockFieldColour ||
          (pageTheme && coloursByTheme[pageTheme][themeColourIndex]) ||
          defaultColour
        );
      },
    }),
    [pageTheme],
  );

  return (
    <PageThemeContext.Provider value={pageThemeProviderValue}>
      {children}
    </PageThemeContext.Provider>
  );
};
